import { supabase } from "@/lib/supabase";

export interface ProductReview {
  id: number;
  product_id: number;
  reviewer_id: string;
  seller_id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  reviewer_name: string;
  reviewer_avatar: string | null;
}

export interface RatingSummary {
  average: number;
  count: number;
}

export const EMPTY_RATING: RatingSummary = { average: 0, count: 0 };

interface RawReview {
  id: number;
  product_id: number;
  reviewer_id: string;
  seller_id: string;
  rating: number;
  comment: string | null;
  created_at: string;
}

/** Calcula promedio y cantidad a partir de una lista de calificaciones. */
export function summarizeRatings(rows: { rating: number }[]): RatingSummary {
  if (rows.length === 0) return EMPTY_RATING;
  const total = rows.reduce((sum, row) => sum + (row.rating ?? 0), 0);
  return { average: total / rows.length, count: rows.length };
}

/** Devuelve el resumen de reputación (estrellas) de un vendedor. */
export async function fetchSellerRating(
  sellerId: string,
): Promise<RatingSummary> {
  if (!sellerId) return EMPTY_RATING;
  const { data, error } = await supabase
    .from("reviews")
    .select("rating")
    .eq("seller_id", sellerId);
  if (error) throw error;
  return summarizeRatings((data ?? []) as { rating: number }[]);
}

/** Devuelve el resumen de reputación para varios vendedores a la vez. */
export async function fetchSellerRatings(
  sellerIds: string[],
): Promise<Map<string, RatingSummary>> {
  const unique = [...new Set(sellerIds.filter(Boolean))];
  const result = new Map<string, RatingSummary>();
  if (unique.length === 0) return result;

  const { data, error } = await supabase
    .from("reviews")
    .select("seller_id, rating")
    .in("seller_id", unique);
  if (error) throw error;

  const grouped = new Map<string, number[]>();
  for (const row of (data ?? []) as { seller_id: string; rating: number }[]) {
    const list = grouped.get(row.seller_id) ?? [];
    list.push(row.rating);
    grouped.set(row.seller_id, list);
  }
  for (const id of unique) {
    result.set(id, summarizeRatings(grouped.get(id) ?? []));
  }
  return result;
}

/** Lista las reseñas de un producto, incluyendo datos del autor. */
export async function fetchProductReviews(
  productId: number,
): Promise<ProductReview[]> {
  const { data, error } = await supabase
    .from("reviews")
    .select(
      "id, product_id, reviewer_id, seller_id, rating, comment, created_at",
    )
    .eq("product_id", productId)
    .order("created_at", { ascending: false });
  if (error) throw error;

  const list = (data ?? []) as RawReview[];
  if (list.length === 0) return [];

  const reviewerIds = [...new Set(list.map((r) => r.reviewer_id))];
  const { data: profilesData, error: profilesError } = await supabase
    .from("profiles")
    .select("id, full_name, email, avatar_url")
    .in("id", reviewerIds);
  if (profilesError) throw profilesError;

  const profilesById = new Map(
    (profilesData ?? []).map((p) => [p.id, p]),
  );

  return list.map((r) => {
    const author = profilesById.get(r.reviewer_id);
    return {
      ...r,
      reviewer_name: author?.full_name ?? author?.email ?? "Usuario",
      reviewer_avatar: author?.avatar_url ?? null,
    };
  });
}

/** Lista las reseñas recibidas por un vendedor, con autor y producto. */
export async function fetchSellerReviews(
  sellerId: string,
  limit = 20,
): Promise<ProductReview[]> {
  if (!sellerId) return [];
  const { data, error } = await supabase
    .from("reviews")
    .select(
      "id, product_id, reviewer_id, seller_id, rating, comment, created_at",
    )
    .eq("seller_id", sellerId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;

  const list = (data ?? []) as RawReview[];
  if (list.length === 0) return [];

  const reviewerIds = [...new Set(list.map((r) => r.reviewer_id))];
  const { data: profilesData, error: profilesError } = await supabase
    .from("profiles")
    .select("id, full_name, email, avatar_url")
    .in("id", reviewerIds);
  if (profilesError) throw profilesError;

  const profilesById = new Map((profilesData ?? []).map((p) => [p.id, p]));

  return list.map((r) => {
    const author = profilesById.get(r.reviewer_id);
    return {
      ...r,
      reviewer_name: author?.full_name ?? author?.email ?? "Usuario",
      reviewer_avatar: author?.avatar_url ?? null,
    };
  });
}

/** Crea o actualiza la reseña del usuario para un producto (una por persona). */
export async function submitReview(input: {
  productId: number;
  sellerId: string;
  reviewerId: string;
  rating: number;
  comment: string;
}): Promise<void> {
  const { error } = await supabase.from("reviews").upsert(
    {
      product_id: input.productId,
      seller_id: input.sellerId,
      reviewer_id: input.reviewerId,
      rating: input.rating,
      comment: input.comment.trim() || null,
    },
    { onConflict: "product_id,reviewer_id" },
  );
  if (error) throw error;
}

/** Elimina la reseña del usuario para un producto. */
export async function deleteReview(
  productId: number,
  reviewerId: string,
): Promise<void> {
  const { error } = await supabase
    .from("reviews")
    .delete()
    .eq("product_id", productId)
    .eq("reviewer_id", reviewerId);
  if (error) throw error;
}